//src/app/features/profile/businessDetails/previewBusinessDetails/index.tsx
import UserSelector from 'app/features/conversations/Components/ConversationRecipients/UserSelector';

import { ScrollContainer } from 'app/layout/dashboardLayout/BoxLayout';
import {
  IUser,
  useAssignBusinessTeamClientMutation,
  useDeleteBusinessTeamClientByIdMutation,
  useGetUserBusinessByIdQuery,
  useGetUsersQuery,
  UserRoleEnum,
  useUpdateBusinessProjectLogoMutation,
  useUpdateBusinessProjectMutation,
} from 'app/services/user';
import useConfirmContinue from 'app/hooks/useConfirmContinue';
import {
  AddIcon,
  AddressIcon,
  ClientSupportIcon,
  EditIcon,
  PhoneIcon,
  ProfileIcon,
  TrashCanSolidIcon,
} from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { USDate } from 'Components/Dates/USDate';
import { statesLookup } from 'states';

import { BusinessProjectGetId } from 'app/services/responseTypes';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import { useCreatePopup } from 'Components/Popups';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import Spinner from 'Components/Spinner';
import Tooltip from 'Components/Tooltips';
import UserAvatar from 'Components/UserAvatar';
import UserBox from 'Components/UserBox';
import { capitalize } from 'lodash';
import { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';

export interface IPreviewBusinessDetailsProps {}

export default function PreviewBusinessDetails(
  props: IPreviewBusinessDetailsProps,
) {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { isClient, isAdmin } = useAuth();

  const { data } = useGetUserBusinessByIdQuery(businessId!);
  const confirmContinue = useConfirmContinue();
  const [UpdateBusinessProject, { isLoading }] =
    useUpdateBusinessProjectMutation();

  const confirmToggleSubscribed = (
    businessId: number,
    key: 'TAX' | 'BK',
    isSubscribed: boolean,
  ) => {
    const updatedSubscribed = {
      ...data?.business?.subscribed,
      [key]: !isSubscribed, // Toggle TAX or BK dynamically
    };

    confirmContinue({
      onConfirm: () =>
        UpdateBusinessProject({
          id: businessId,
          business: { subscribed: updatedSubscribed },
        })
          .unwrap()
          .then(() => {
            successToast(
              `Business status updated: ${key} is now ${
                !isSubscribed ? 'Subscribed' : 'Not Subscribed'
              }`,
            );
          })
          .catch(() => errorToast(`Failed to update ${key} status`)),
      theme: 'succes',
      confirmText: 'Confirm',
      title: `Change ${key} Subscription Status`,
      text: `Are you sure you want to change the ${key} status to ${
        !isSubscribed ? 'Subscribed' : 'Not Subscribed'
      }?`,
    });
  };

  if (!data) return <Spinner />;

  return (
    <div className="h-screen overflow-y-auto">
      <div className="grid gap-2 border-b border-gray-100">
        <div className="text-gray-dark font-semibold items-center gap-4 flex text-medium capitalize p-4">
          <BusinessLogo
            businessId={data.business.id}
            logo_url={data.business.logo_url}
          />

          <div className="flex items-center gap-2">
            {data.business.name}

            {['TAX', 'BK'].map((key) => (
              <span
                key={key}
                className="text-xs font-semibold px-2 py-1 rounded-md cursor-pointer"
                style={
                  data.business.subscribed[key]
                    ? { color: '#16a34a', backgroundColor: '#d1fae5' } // Green for Subscribed
                    : { color: '#dc2626', backgroundColor: '#fee2e2' } // Red for Not Subscribed
                }
                onClick={() =>
                  isAdmin &&
                  confirmToggleSubscribed(
                    data.business.id,
                    key as 'TAX' | 'BK',
                    data.business.subscribed[key] ?? false,
                  )
                }
              >
                {data.business.subscribed[key]
                  ? `${key} Subscribed`
                  : `${key} Not Subscribed`}
              </span>
            ))}
          </div>

          {(isClient || isAdmin) && (
            <Button
              className="max-w-min self-start ml-auto"
              onClick={() => navigate(`edit`)}
            >
              Edit <EditIcon />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[auto,1fr,1fr] p-4 md-max:grid-cols-1 overflow-y-auto gap-4">
        <Section title="Business Details">
          <InfoSection children={['EIN', data?.business.ein]} />
          <InfoSection
            children={['Industry', data?.business.specialization?.name]}
          />

          <InfoSection children={['Type', data?.business.business_type]} />

          <InfoSection
            children={[
              'Month',
              data?.business.fiscal_year_start && (
                <span className="capitalize">
                  {data?.business.fiscal_year_start}
                </span>
              ),
            ]}
          />
          <InfoSection
            children={[
              'Formation Date',
              data?.business.formation_date && (
                <USDate date={data?.business.formation_date} />
              ),
            ]}
          />

          <InfoSection
            children={[
              'Formation State',
              statesLookup[data?.business.formation_state]?.label,
            ]}
          />
          <InfoSection
            children={[
              'Bookkeeping Software',
              data?.business.bookkeeping_software &&
                capitalize(data?.business.bookkeeping_software).replace(
                  '_',
                  ' ',
                ),
            ]}
          />
          <InfoSection
            children={[
              'Owned by',
              capitalize(data?.business.ownership).replace('_', ' '),
            ]}
          />
          <InfoSection
            children={[
              'Operational This Year',
              data?.business.operational === true
                ? 'Yes'
                : data?.business.operational === false
                ? 'No'
                : undefined,
            ]}
          />
        </Section>

        <ScrollContainer className="shadow rounded-md overflow-y-auto">
          <Title>Business Owners</Title>

          <ScrollContainer>
            {!data?.business.business_owners.length && (
              <div className="text-gray-light text-sm p-4">N/A</div>
            )}
            {data?.business.business_owners.map((b) => (
              <div
                className="p-2 flex gap-2 border-b border-gray-100"
                key={b.id}
              >
                {b.user && (
                  <UserBox
                    border=""
                    px=""
                    py=""
                    shadow=""
                    user={b.user}
                    theme="white"
                    size="xs"
                    content={
                      <div className="text-xs text-gray-700">
                        {parseInt(b.ownership_percentage) || 'N/A'}
                        {b.ownership_percentage && '%'} {' Ownership'}
                      </div>
                    }
                  />
                )}
                {!b.user && (
                  <>
                    <ProfileIcon className="w-5 h-5 text-gray-light" />

                    <div>
                      <div className="text-gray-dark text-sm font-semibold break-all">
                        {b.firstname || 'N/A'} {b.lastname || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-700">
                        {parseInt(b.ownership_percentage) || 'N/A'}
                        {b.ownership_percentage && '%'} {' Ownership'}
                      </div>
                      <div className="text-xs text-gray-700">
                        {'SSN:'}
                        {b.ssn || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-700 break-all">
                        {b.address || 'N/A'}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </ScrollContainer>
        </ScrollContainer>

        <BusinessTeam business={data.business} />
      </div>
    </div>
  );
}

function BusinessTeam({ business }: { business: BusinessProjectGetId }) {
  const [DeleteBusinessTeam] = useDeleteBusinessTeamClientByIdMutation();
  const [AssignBusinessTeam] = useAssignBusinessTeamClientMutation();

  const addTeamMember = async (allUsers: IUser[]) => {
    return AssignBusinessTeam({
      business_team: {
        business_id: business.id!,
        assignee_ids: allUsers.map((t) => t.id),
      },
    })
      .unwrap()
      .then(() => {
        successToast('Business default team updated');
      })
      .catch(() => {
        errorToast("Couldn't update team");
      });
  };
  return (
    <TeamSelector
      title="Business Default Team"
      team={business.team_members}
      onConfirm={addTeamMember}
      onDeleteMember={(u) => DeleteBusinessTeam([business.id, u.id]).unwrap()}
    />
  );
}

export function TeamSelector({
  team,
  title,
  onDeleteMember,
  onConfirm,
}: {
  team: IUser[];
  title?: ReactNode;
  onDeleteMember: (u: IUser) => Promise<any>;
  onConfirm: (newTeam: IUser[]) => Promise<any>;
}) {
  const SelectorPopup = useCreatePopup();
  const confirmDelete = useConfirmDeleteModal();
  const { isAdmin } = useAuth();

  const { data: users } = useGetUsersQuery(
    { roles: [UserRoleEnum.ACCOUNTANT, UserRoleEnum.ADMIN] },
    { skip: !isAdmin },
  );
  return (
    <>
      <ScrollContainer className="shadow rounded-md md-max:overflow-visible">
        <div className="text-green w-full text-sm font-semibold p-2 border-b self-start  border-gray-100">
          <div className="flex justify-between items-center mr-4">
            <span>{title}</span>
            {isAdmin && (
              <button
                onClick={SelectorPopup.open}
                className="max-w-min "
                ref={SelectorPopup.setAnchor}
              >
                <AddIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <ScrollContainer>
          {!team.length && (
            <div className="text-gray-light text-sm p-4">N/A</div>
          )}
          {team.map((u) => (
            <div className="border-b border-gray-100 px-2" key={u.id}>
              <UserBox
                size="xs"
                theme="white"
                px=""
                py=""
                shadow=""
                user={u}
                right={
                  isAdmin && (
                    <Button
                      theme="red-outline"
                      className="max-w-min"
                      onClick={async () =>
                        confirmDelete('Team Assignee', () => onDeleteMember(u))
                      }
                    >
                      <TrashCanSolidIcon className="w-4 h-4 text-red-200" />
                    </Button>
                  )
                }
              />
            </div>
          ))}
        </ScrollContainer>
      </ScrollContainer>
      <SelectorPopup.Component overlay={true}>
        <OverlayWindow paddingSize="p-1" size={WindowSize.sm}>
          <UserSelector
            canDeleteLocked={true}
            lockedSelections={team!}
            userSelections={users!}
            onConfirm={(users) =>
              onConfirm(users).finally(() => SelectorPopup.close())
            }
          />
        </OverlayWindow>
      </SelectorPopup.Component>
    </>
  );
}

function Section({
  title,
  children,
  className,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`shadow rounded-md  ${className}`}>
      <Title>{title}</Title>
      <ScrollContainer className="grid min-w-[140px] grid-cols-[auto,auto] content-start p-4 items-center  gap-4">
        {children}
      </ScrollContainer>
    </div>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <div className="text-green w-full text-sm font-semibold p-2 border-b self-start  border-gray-100">
      {children}
    </div>
  );
}

function InfoSection({
  children,
}: {
  children: (ReactNode | string | undefined)[];
}) {
  const [label, data] = children;
  return (
    <>
      <div className="text-xs text-gray-700">{label}</div>
      {data && (
        <div className=" text-gray-dark text-sm font-medium">{data}</div>
      )}
      {!data && <div className=" text-gray-700 font-normal text-xs ">N/A</div>}
    </>
  );
}

export function BusinessLogo({
  logo_url,
  disabled,
  businessId,
}: {
  logo_url?: string;
  businessId?: number;
  disabled?: boolean;
}) {
  const [UpdateBusinessProjectLogo, { isLoading }] =
    useUpdateBusinessProjectLogoMutation();

  if (isLoading)
    return (
      <div className="w-12 h-12 shadow self-start rounded-md flex justify-center items-center">
        <Spinner bounce={false} />
      </div>
    );
  return (
    <label className={`cursor-pointer w-12 h-12 shadow rounded-md self-start`}>
      <Tooltip message={!disabled && 'Upload business logo'}>
        <input
          hidden
          type="file"
          disabled={disabled}
          onChange={(e) => {
            e.stopPropagation();
            if (e.target.files && e.target.files[0]) {
              let form = new FormData();
              form.append('business[logo]', e.target.files[0]);

              UpdateBusinessProjectLogo({
                id: businessId,
                business: form,
              })
                .unwrap()
                .then(() => {
                  successToast('Business logo updated');
                });
            }
          }}
        />

        {!logo_url && (
          <div className="shadow w-12 rounded-md h-12 flex items-center justify-center">
            <ClientSupportIcon className=" text-gray-light/50 h-8 w-8" />
          </div>
        )}
        {logo_url && (
          <UserAvatar
            user={{ picture_url: logo_url }}
            rounded={'  rounded-md'}
            size={'w-12 h-12 md-max:h-7 md-max:w-7'}
          />
        )}
      </Tooltip>
    </label>
  );
}
